import React, { useEffect, useMemo, useState } from 'react';
import { Button, LoadingSpinner } from '../atoms';
import { resolveAssetUrl } from '../../utils/url';
import type {
  PreviewImageResponse,
  PreviewImageMetadataRequest,
} from '../../types/api/movie';

export interface PreviewImagesSectionProps {
  title?: string;
  images: PreviewImageResponse[];
  maxPerUpload?: number;
  maxTotal?: number;
  /**
   * Optional flag to indicate a parent-level busy state.
   * Local actions will also manage their own loading indicators.
   */
  isBusy?: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onUpdateMetadata: (
    imageId: string,
    metadata: PreviewImageMetadataRequest
  ) => Promise<void>;
  onReplaceImage: (
    imageId: string,
    metadata: PreviewImageMetadataRequest,
    file?: File
  ) => Promise<void>;
}

type EditablePreviewImage = PreviewImageResponse & {
  draftAltText: string;
  draftSortOrder: string;
  isSavingMetadata: boolean;
  isReplacingImage: boolean;
};

export const PreviewImagesSection: React.FC<PreviewImagesSectionProps> = ({
  title = 'Preview Images',
  images,
  maxPerUpload = 5,
  maxTotal = 10,
  isBusy,
  onUpload,
  onUpdateMetadata,
  onReplaceImage,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [localImages, setLocalImages] = useState<EditablePreviewImage[]>([]);

  useEffect(() => {
    const mapped: EditablePreviewImage[] = (images || []).map((img) => ({
      ...img,
      draftAltText: img.altText ?? '',
      draftSortOrder:
        typeof img.sortOrder === 'number' && !Number.isNaN(img.sortOrder)
          ? String(img.sortOrder)
          : '',
      isSavingMetadata: false,
      isReplacingImage: false,
    }));
    setLocalImages(mapped);
  }, [images]);

  const currentCount = images?.length ?? 0;
  const remainingSlots = useMemo(
    () => Math.max(0, maxTotal - currentCount),
    [maxTotal, currentCount]
  );

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files || []);
    setFileError(null);

    if (files.length === 0) {
      setSelectedFiles([]);
      return;
    }

    if (files.length > maxPerUpload) {
      setFileError(`You can upload up to ${maxPerUpload} images per request.`);
      setSelectedFiles(files.slice(0, maxPerUpload));
      return;
    }

    if (remainingSlots <= 0) {
      setFileError(`Maximum of ${maxTotal} preview images reached.`);
      setSelectedFiles([]);
      return;
    }

    if (files.length > remainingSlots) {
      setFileError(
        `You can upload only ${remainingSlots} more image${remainingSlots > 1 ? 's' : ''} (max ${maxTotal} total).`
      );
      setSelectedFiles(files.slice(0, remainingSlots));
      return;
    }

    const invalidFile = files.find(
      (file) => file.type && !file.type.startsWith('image/')
    );
    if (invalidFile) {
      setFileError('Please select image files only.');
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0) {
      setFileError('Please select at least one image to upload.');
      return;
    }

    setIsUploading(true);
    setFileError(null);
    try {
      await onUpload(selectedFiles);
      setSelectedFiles([]);
    } catch (error: any) {
      setFileError(error?.message || 'Failed to upload preview images.');
    } finally {
      setIsUploading(false);
    }
  };

  const updateLocalImageDraft = (
    previewImageId: string,
    changes: Partial<EditablePreviewImage>
  ) => {
    setLocalImages((prev) =>
      prev.map((img) =>
        img.previewImageId === previewImageId ? { ...img, ...changes } : img
      )
    );
  };

  const handleSaveMetadata = async (img: EditablePreviewImage) => {
    const sortOrder =
      img.draftSortOrder.trim().length > 0
        ? Number.parseInt(img.draftSortOrder, 10)
        : undefined;

    if (
      img.draftSortOrder.trim().length > 0 &&
      (Number.isNaN(sortOrder) || sortOrder === undefined)
    ) {
      updateLocalImageDraft(img.previewImageId, {
        isSavingMetadata: false,
      });
      return;
    }

    const payload: PreviewImageMetadataRequest = {
      altText: img.draftAltText.trim() || undefined,
      sortOrder,
    };

    updateLocalImageDraft(img.previewImageId, { isSavingMetadata: true });
    try {
      await onUpdateMetadata(img.previewImageId, payload);
    } finally {
      updateLocalImageDraft(img.previewImageId, { isSavingMetadata: false });
    }
  };

  const handleReplaceImage = async (
    img: EditablePreviewImage,
    file: File | undefined
  ) => {
    if (!file) {
      return;
    }

    const sortOrder =
      img.draftSortOrder.trim().length > 0
        ? Number.parseInt(img.draftSortOrder, 10)
        : undefined;

    const payload: PreviewImageMetadataRequest = {
      altText: img.draftAltText.trim() || undefined,
      sortOrder,
    };

    updateLocalImageDraft(img.previewImageId, { isReplacingImage: true });
    try {
      await onReplaceImage(img.previewImageId, payload, file);
    } finally {
      updateLocalImageDraft(img.previewImageId, { isReplacingImage: false });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500">
          {currentCount}/{maxTotal} images used
        </p>
      </div>

      <p className="text-sm text-gray-600 text-left">
        Upload up to {maxPerUpload} images per request. A maximum of {maxTotal} preview
        images are allowed per content item.
      </p>

      <div className="space-y-4">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-left text-gray-700">
            Add Preview Images
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            disabled={isUploading || isBusy || remainingSlots <= 0}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
          />
          {fileError && (
            <p className="text-xs text-red-600 mt-1.5 text-left">{fileError}</p>
          )}
          <p className="text-xs text-gray-500">
            JPEG, PNG, or WebP. Up to {maxPerUpload} images per upload,{' '}
            {maxTotal} total.
          </p>

          {selectedFiles.length > 0 && (
            <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1">
              <p className="font-medium">
                Selected images ({selectedFiles.length}
                {remainingSlots > 0 ? ` / remaining slots: ${remainingSlots}` : ''})
              </p>
              <ul className="list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                {selectedFiles.map((file) => (
                  <li key={file.name} className="truncate">
                    {file.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedFiles([]);
                setFileError(null);
              }}
              disabled={isUploading || selectedFiles.length === 0}
            >
              Clear Selection
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleUploadClick}
              disabled={
                isUploading || isBusy || selectedFiles.length === 0 || remainingSlots <= 0
              }
            >
              {isUploading ? 'Uploading...' : 'Upload Images'}
            </Button>
          </div>
        </div>

        <div>
          {isBusy && localImages.length === 0 ? (
            <div className="flex items-center justify-center min-h-[140px]">
              <LoadingSpinner size="md" />
            </div>
          ) : localImages.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
              No preview images yet. Use the uploader on the left to add images.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {localImages.map((img) => (
                <div
                  key={img.previewImageId}
                  className="flex gap-3 border border-gray-200 rounded-lg p-3 bg-white"
                >
                  <div className="flex-shrink-0 w-24 h-32 rounded-md overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                      src={resolveAssetUrl(img.imageUrl)}
                      alt={img.altText || 'Preview image'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="flex-1 space-y-2 text-left">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-gray-800 truncate">
                        {img.altText || 'No caption'}
                      </p>
                      {img.uploadedAt && (
                        <p className="text-[11px] text-gray-400">
                          {new Date(img.uploadedAt).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="block text-[11px] font-medium text-gray-700">
                          Caption
                        </label>
                        <input
                          type="text"
                          value={img.draftAltText}
                          onChange={(e) =>
                            updateLocalImageDraft(img.previewImageId, {
                              draftAltText: e.target.value,
                            })
                          }
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]/30 focus:outline-none"
                          placeholder="Optional alt text"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[11px] font-medium text-gray-700">
                          Sort Order
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={img.draftSortOrder}
                          onChange={(e) =>
                            updateLocalImageDraft(img.previewImageId, {
                              draftSortOrder: e.target.value,
                            })
                          }
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]/30 focus:outline-none"
                          placeholder="Auto"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleSaveMetadata(img)}
                        disabled={img.isSavingMetadata || img.isReplacingImage || isBusy}
                      >
                        {img.isSavingMetadata ? 'Saving...' : 'Save Metadata'}
                      </Button>

                      <label className="inline-flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer">
                        <span>Replace image</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) =>
                            handleReplaceImage(
                              img,
                              e.target.files ? e.target.files[0] : undefined
                            )
                          }
                        />
                        {img.isReplacingImage && (
                          <span className="text-[11px] text-gray-500">Uploading...</span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


